import { defineMessages } from 'react-intl';

import type { OrderAppError } from '@/graphql/orderApp/api';
import { INTL_PREFIX } from '@/messages/const';

import type { FormattedError } from './common';
import { commonErrorCodeMessages } from './common';

const intlPrefix = `${INTL_PREFIX.ORDER_APP}`;

export const errors = defineMessages({
  preordersMismatch: {
    id: `${intlPrefix}.preordersMismatch`,
    defaultMessage:
      'This product is out of stock or no longer available for purchase',
  },
  maxItemCount: {
    id: `${intlPrefix}.maxItemCount`,
    defaultMessage: 'Exceeded max items in the shopping bag.',
  },
  maxWatchesCount: {
    id: `${intlPrefix}.maxWatchesCount`,
    defaultMessage: 'Exceeded max watches in the shopping bag.',
  },
  maxWatchesSameReferenceCount: {
    id: `checkout.addPopin.exceeded`,
    defaultMessage:
      'Exceeded max watches with same reference in the shopping bag.',
  },
  maxSunglassesSameReferenceCount: {
    id: `checkout.addPopin.sunglasses.exceeded`,
    defaultMessage:
      'Exceeded max sunglasses with same reference in the shopping bag.',
  },
  maxWatchesSameModelCount: {
    id: `${intlPrefix}.maxWatchesSameModelCount`,
    defaultMessage:
      'Exceeded max watches with the same model in the shopping bag.',
  },
  maxWatchesSameCollectionCount: {
    id: `${intlPrefix}.maxWatchesSameCollectionCount`,
    defaultMessage:
      'Exceeded max watches with same collection in the shopping bag. ',
  },
  maxStrapsCount: {
    id: `${intlPrefix}.maxStrapsCount`,
    defaultMessage: 'Exceeded max straps in the shopping bag.',
  },
  maxStrapsSameReferenceCount: {
    id: `${intlPrefix}.maxStrapsSameReferenceCount`,
    defaultMessage:
      'Exceeded max straps with same reference in the shopping bag.',
  },
  mixOrderTypesError: {
    id: `${intlPrefix}.mixOrderTypesError`,
    defaultMessage: 'Mixing regular products with preorders is not allowed.',
  },
  maxItemForPreorderCount: {
    id: `${intlPrefix}.maxItemForPreorderCount`,
    defaultMessage: 'Exceeded max preorder products in the shopping bag.',
  },
  priceCheckFailed: {
    id: `${intlPrefix}.priceCheckFailed`,
    defaultMessage:
      'The price of an item in your cart has changed. Please add it to your cart again to reflect its current price.',
  },
  strapPackAlreadyAdded: {
    id: `${intlPrefix}.strapPackAlreadyAdded`,
    defaultMessage: 'You can add only one strap pack of specific type.',
  },
  strapPackNoCTAProducts: {
    id: `${intlPrefix}.strapPackNoCTAProducts`,
    defaultMessage: 'NO_CTA products are not available for checkout or order!',
  },
});

export const shoppingBagErrorCodes = [
  'price.cpi_mismatch',
  'stock.cpi_mismatch',
];

export const getOrderAppErrorCodeMessage = (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  error: FormattedError<OrderAppError>,
) => {
  switch (error.code) {
    case 'stock.cpi_mismatch':
      return errors.preordersMismatch;

    case 'max.item.count':
      return errors.maxItemCount;

    case 'max.watches.count':
      return errors.maxWatchesCount;

    case 'max.watches.same_reference.count':
      return errors.maxWatchesSameReferenceCount;

    case 'max.eyewears.same_reference.count':
      return errors.maxSunglassesSameReferenceCount;

    case 'max.watches.same_model.count':
      return errors.maxWatchesSameModelCount;

    case 'max.watches.same_collection.count':
      return errors.maxWatchesSameCollectionCount;

    case 'max.straps.count':
      return errors.maxStrapsCount;

    case 'max.straps.same_reference.count':
      return errors.maxStrapsSameReferenceCount;

    case 'mix.order_types.error':
      return errors.mixOrderTypesError;

    case 'max.item_for_preorder.count':
      return errors.maxItemForPreorderCount;

    case 'price.cpi_mismatch':
      return errors.priceCheckFailed;

    case 'strap_pack.already_added':
      return errors.strapPackAlreadyAdded;

    case 'no_cta.order.error':
      return errors.strapPackNoCTAProducts;

    default:
      return !error.message
        ? commonErrorCodeMessages.unexpectedErrorOccurred
        : error.message;
  }
};
